import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

// Import dashboard components
import BookingCalendar from './components/BookingCalendar';
import AnalyticsPanel from './components/AnalyticsPanel';
import RevenueTracker from './components/RevenueTracker';
import BookingManagement from './components/BookingManagement';
import CourtConfiguration from './components/CourtConfiguration';
import CustomerManagement from './components/CustomerManagement';
import FinancialReports from './components/FinancialReports';
import MaintenanceScheduler from './components/MaintenanceScheduler';
import MarketingTools from './components/MarketingTools';

const VenueOwnerDashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const navigate = useNavigate();

  // Mock data initialization
  useEffect(() => {
    const initializeDashboard = () => {
      const mockData = {
        venue: {
          id: 1,
          name: 'Arena Sports Center',
          address: 'Rua das Quadras, 123 - Vila Olímpia, São Paulo',
          totalCourts: 6,
          activeBookings: 28,
          rating: 4.7,
          totalReviews: 284
        },
        todaysStats: {
          totalBookings: 15,
          occupancyRate: 78,
          dailyRevenue: 2850.50,
          pendingApprovals: 3,
          maintenanceAlerts: 1
        },
        weeklyStats: {
          totalBookings: 89,
          occupancyRate: 72,
          totalRevenue: 18420.75,
          averageBookingValue: 206.97,
          customerSatisfaction: 4.6
        },
        monthlyStats: {
          totalBookings: 342,
          occupancyRate: 68,
          totalRevenue: 71850.25,
          newCustomers: 47,
          returnCustomers: 168
        },
        courts: [
        {
          id: 1,
          name: 'Quadra 1 - Society',
          type: 'society',
          status: 'available',
          surface: 'Grama Sintética',
          lighting: true,
          capacity: 14,
          hourlyRate: 120.00,
          nextMaintenance: '2025-10-25',
          bookingsToday: 4,
          revenueToday: 480.00,
          image: "https://images.unsplash.com/photo-1644628967071-fcfb7de49d4f",
          imageAlt: 'Well-maintained soccer field with synthetic grass and professional lighting'
        },
        {
          id: 2,
          name: 'Quadra 2 - Futsal',
          type: 'futsal',
          status: 'occupied',
          surface: 'Piso Sintético',
          lighting: true,
          capacity: 10,
          hourlyRate: 100.00,
          nextMaintenance: '2025-10-20',
          bookingsToday: 6,
          revenueToday: 600.00,
          image: "https://images.unsplash.com/photo-1701272873248-ee041b51b02b",
          imageAlt: 'Indoor futsal court with wooden flooring and professional lighting system'
        },
        {
          id: 3,
          name: 'Quadra 3 - Beach Tennis',
          type: 'beach_tennis',
          status: 'maintenance',
          surface: 'Areia',
          lighting: true,
          capacity: 4,
          hourlyRate: 80.00,
          nextMaintenance: '2025-10-18',
          bookingsToday: 0,
          revenueToday: 0.00,
          image: "https://images.unsplash.com/photo-1435984513325-956032a33229",
          imageAlt: 'Beach tennis court with sand surface and blue and white net setup'
        }],

        recentBookings: [
        {
          id: 101,
          customerName: 'João Silva',
          customerAvatar: 'https://images.unsplash.com/photo-1641479160067-5ae7bde244b0',
          customerAvatarAlt: 'Professional headshot of young man with short brown hair in casual shirt',
          courtName: 'Quadra 1',
          date: '2025-10-16',
          time: '14:00 - 16:00',
          duration: 2,
          amount: 240.00,
          status: 'confirmed',
          paymentStatus: 'paid',
          players: 12,
          contactInfo: {
            phone: '(11) 99999-1234',
            email: 'joao.silva@email.com'
          }
        },
        {
          id: 102,
          customerName: 'Maria Santos',
          customerAvatar: 'https://images.unsplash.com/photo-1628013663808-25ef6a3b28b4',
          customerAvatarAlt: 'Professional headshot of woman with long brown hair in business attire',
          courtName: 'Quadra 2',
          date: '2025-10-16',
          time: '18:00 - 20:00',
          duration: 2,
          amount: 200.00,
          status: 'pending_approval',
          paymentStatus: 'pending',
          players: 10,
          contactInfo: {
            phone: '(11) 98888-5678',
            email: 'maria.santos@email.com'
          }
        },
        {
          id: 103,
          customerName: 'Pedro Oliveira',
          customerAvatar: 'https://images.unsplash.com/photo-1667575949231-fbf430640797',
          customerAvatarAlt: 'Professional headshot of man with beard in blue shirt',
          courtName: 'Quadra 1',
          date: '2025-10-17',
          time: '10:00 - 12:00',
          duration: 2,
          amount: 240.00,
          status: 'confirmed',
          paymentStatus: 'paid',
          players: 14,
          contactInfo: {
            phone: '(11) 97777-9012',
            email: 'pedro.oliveira@email.com'
          }
        }],

        customers: [
        {
          id: 1,
          name: 'João Silva',
          avatar: 'https://images.unsplash.com/photo-1641479160067-5ae7bde244b0',
          avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt',
          email: 'joao.silva@email.com',
          phone: '(11) 99999-1234',
          totalBookings: 28,
          totalSpent: 3360.00,
          averageRating: 4.8,
          lastBooking: '2025-10-14',
          joinDate: '2024-03-15',
          status: 'vip'
        },
        {
          id: 2,
          name: 'Maria Santos',
          avatar: 'https://images.unsplash.com/photo-1628013663808-25ef6a3b28b4',
          avatarAlt: 'Professional headshot of woman with long brown hair in business attire',
          email: 'maria.santos@email.com',
          phone: '(11) 98888-5678',
          totalBookings: 15,
          totalSpent: 1800.00,
          averageRating: 4.5,
          lastBooking: '2025-10-12',
          joinDate: '2024-06-20',
          status: 'regular'
        }],

        analytics: {
          occupancyTrend: [
          { date: '2025-10-09', occupancy: 65 },
          { date: '2025-10-10', occupancy: 72 },
          { date: '2025-10-11', occupancy: 58 },
          { date: '2025-10-12', occupancy: 81 },
          { date: '2025-10-13', occupancy: 76 },
          { date: '2025-10-14', occupancy: 69 },
          { date: '2025-10-15', occupancy: 84 }],

          revenueData: [
          { month: 'Jun', revenue: 52340.00 },
          { month: 'Jul', revenue: 61250.50 },
          { month: 'Ago', revenue: 58920.75 },
          { month: 'Set', revenue: 67480.25 },
          { month: 'Out', revenue: 71850.25 }],

          peakHours: [
          { time: '18:00-19:00', bookings: 42 },
          { time: '19:00-20:00', bookings: 38 },
          { time: '17:00-18:00', bookings: 35 },
          { time: '20:00-21:00', bookings: 31 },
          { time: '16:00-17:00', bookings: 28 }]

        },
        maintenance: [
        {
          id: 1,
          courtId: 3,
          courtName: 'Quadra 3 - Beach Tennis',
          type: 'Troca de areia',
          status: 'in_progress',
          scheduledDate: '2025-10-18',
          estimatedDuration: 4,
          cost: 800.00,
          provider: 'Manutenção Sports Ltda',
          priority: 'high'
        },
        {
          id: 2,
          courtId: 1,
          courtName: 'Quadra 1 - Society',
          type: 'Manutenção da iluminação',
          status: 'scheduled',
          scheduledDate: '2025-10-25',
          estimatedDuration: 2,
          cost: 350.00,
          provider: 'Elétrica Central',
          priority: 'medium'
        }]

      };

      setDashboardData(mockData);
      setIsLoading(false);
    };

    // Simulate loading delay
    const timer = setTimeout(initializeDashboard, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleBookingAction = (bookingId, action) => {
    setDashboardData((prev) => ({
      ...prev,
      recentBookings: prev?.recentBookings?.map((booking) =>
      booking?.id === bookingId ?
      { ...booking, status: action === 'approve' ? 'confirmed' : 'cancelled' } :
      booking
      )
    }));
  };

  const handleCourtStatusUpdate = (courtId, status) => {
    setDashboardData((prev) => ({
      ...prev,
      courts: prev?.courts?.map((court) =>
      court?.id === courtId ? { ...court, status } : court
      )
    }));
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <BookingCalendar
              courts={dashboardData?.courts}
              bookings={dashboardData?.recentBookings} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RevenueTracker stats={dashboardData?.todaysStats} />
              <AnalyticsPanel analytics={dashboardData?.analytics} />
            </div>
          </div>);

      case 'bookings':
        return (
          <BookingManagement
            bookings={dashboardData?.recentBookings}
            courts={dashboardData?.courts}
            onBookingAction={handleBookingAction} />);


      case 'courts':
        return (
          <CourtConfiguration
            courts={dashboardData?.courts}
            onStatusUpdate={handleCourtStatusUpdate} />);


      case 'customers':
        return (
          <CustomerManagement
            customers={dashboardData?.customers}
            bookings={dashboardData?.recentBookings} />);


      case 'reports':
        return (
          <FinancialReports
            weeklyStats={dashboardData?.weeklyStats}
            monthlyStats={dashboardData?.monthlyStats}
            analytics={dashboardData?.analytics} />);


      case 'maintenance':
        return (
          <MaintenanceScheduler
            maintenance={dashboardData?.maintenance}
            courts={dashboardData?.courts} />);


      case 'marketing':
        return (
          <MarketingTools
            venue={dashboardData?.venue}
            analytics={dashboardData?.analytics} />);


      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-muted rounded w-1/2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)]?.map((_, i) =>
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
              )}
            </div>
            <div className="h-96 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              {dashboardData?.venue?.name} 🏟️
            </h1>
            <p className="text-muted-foreground mb-2">
              {dashboardData?.venue?.address}
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={16} className="text-warning" />
                <span className="font-medium">{dashboardData?.venue?.rating}</span>
                <span className="text-muted-foreground">({dashboardData?.venue?.totalReviews} avaliações)</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span className="text-muted-foreground">{dashboardData?.venue?.totalCourts} quadras</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <Input
                type="text"
                placeholder="Buscar reservas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-64"
                iconName="Search"
                iconSize={16} />

            </div>
            
            <Select
              value={selectedDateRange}
              onChange={(value) => setSelectedDateRange(value)}
              className="w-40">

              <option value="today">Hoje</option>
              <option value="week">Esta Semana</option>
              <option value="month">Este Mês</option>
              <option value="custom">Personalizado</option>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Reservas Hoje</p>
                <p className="text-2xl font-heading font-bold text-card-foreground">
                  {dashboardData?.todaysStats?.totalBookings}
                </p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Taxa de Ocupação</p>
                <p className="text-2xl font-heading font-bold text-card-foreground">
                  {dashboardData?.todaysStats?.occupancyRate}%
                </p>
              </div>
              <div className="p-2 bg-success/10 rounded-lg">
                <Icon name="TrendingUp" size={20} className="text-success" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Receita Hoje</p>
                <p className="text-2xl font-heading font-bold text-card-foreground">
                  R$ {dashboardData?.todaysStats?.dailyRevenue?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-2 bg-success/10 rounded-lg">
                <Icon name="DollarSign" size={20} className="text-success" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-heading font-bold text-card-foreground">
                  {dashboardData?.todaysStats?.pendingApprovals}
                </p>
              </div>
              <div className="p-2 bg-warning/10 rounded-lg">
                <Icon name="Clock" size={20} className="text-warning" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Manutenção</p>
                <p className="text-2xl font-heading font-bold text-card-foreground">
                  {dashboardData?.todaysStats?.maintenanceAlerts}
                </p>
              </div>
              <div className="p-2 bg-destructive/10 rounded-lg">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-6">
          <nav className="flex space-x-8 overflow-x-auto">
            {[
            { id: 'overview', label: 'Visão Geral', icon: 'BarChart3' },
            { id: 'bookings', label: 'Reservas', icon: 'Calendar' },
            { id: 'courts', label: 'Quadras', icon: 'MapPin' },
            { id: 'customers', label: 'Clientes', icon: 'Users' },
            { id: 'reports', label: 'Relatórios', icon: 'FileText' },
            { id: 'maintenance', label: 'Manutenção', icon: 'Tool' },
            { id: 'marketing', label: 'Marketing', icon: 'Megaphone' }]?.
            map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
              activeTab === tab?.id ?
              'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'}`
              }>

                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {getTabContent()}
        </div>
      </main>
    </div>);

};

export default VenueOwnerDashboard;